
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DownloadCloud, Eye, FileText, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Sample invoice data
const invoicesData = [
  {
    id: 'INV-001',
    supplier: 'Atlantic Electric',
    amount: 3450.75,
    date: '2024-09-10',
    dueDate: '2024-10-10',
    status: 'Paid',
    type: 'Electricity',
  },
  {
    id: 'INV-002',
    supplier: 'South Jersey Gas',
    amount: 1250.30,
    date: '2024-09-15',
    dueDate: '2024-10-15',
    status: 'Pending',
    type: 'Gas',
  },
  {
    id: 'INV-003',
    supplier: 'Cape May Water Utility',
    amount: 875.45,
    date: '2024-09-05',
    dueDate: '2024-10-05',
    status: 'Paid',
    type: 'Water',
  },
  {
    id: 'INV-004',
    supplier: 'Atlantic Electric',
    amount: 3200.20,
    date: '2024-08-10',
    dueDate: '2024-09-10',
    status: 'Paid',
    type: 'Electricity',
  },
  {
    id: 'INV-005',
    supplier: 'South Jersey Gas',
    amount: 1100.90,
    date: '2024-08-15',
    dueDate: '2024-09-15',
    status: 'Overdue',
    type: 'Gas',
  },
];

interface InvoiceTableProps {
  className?: string;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({ className }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter invoices based on search term
  const filteredInvoices = invoicesData.filter((invoice) => {
    const searchText = searchTerm.toLowerCase();
    return (
      invoice.id.toLowerCase().includes(searchText) ||
      invoice.supplier.toLowerCase().includes(searchText) ||
      invoice.type.toLowerCase().includes(searchText) ||
      invoice.status.toLowerCase().includes(searchText)
    );
  });

  // Function to get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Paid':
        return <Badge className="bg-energy-green text-white">Paid</Badge>;
      case 'Pending':
        return <Badge className="bg-energy-blue text-white">Pending</Badge>;
      case 'Overdue':
        return <Badge className="bg-energy-red text-white">Overdue</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Function to get type badge
  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'Electricity':
        return <Badge variant="outline" className="border-energy-blue text-energy-blue">Electricity</Badge>;
      case 'Gas':
        return <Badge variant="outline" className="border-energy-red text-energy-red">Gas</Badge>;
      case 'Water':
        return <Badge variant="outline" className="border-energy-lightBlue text-energy-lightBlue">Water</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader className="px-6 py-4 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold">Recent Invoices</CardTitle>
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search invoices..."
              className="pl-8 h-9 w-[200px] bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline" size="sm" className="h-9">
            <FileText className="h-4 w-4 mr-1" />
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-0 py-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[100px] font-medium">Invoice ID</TableHead>
                <TableHead className="font-medium">Supplier</TableHead>
                <TableHead className="font-medium">Type</TableHead>
                <TableHead className="font-medium">Amount</TableHead>
                <TableHead className="font-medium">Date</TableHead>
                <TableHead className="font-medium">Due Date</TableHead>
                <TableHead className="font-medium">Status</TableHead>
                <TableHead className="font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.map((invoice) => (
                <TableRow 
                  key={invoice.id}
                  className="group hover:bg-muted/50 transition-colors"
                >
                  <TableCell className="font-medium">{invoice.id}</TableCell>
                  <TableCell>{invoice.supplier}</TableCell>
                  <TableCell>{getTypeBadge(invoice.type)}</TableCell>
                  <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                  <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(invoice.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <DownloadCloud className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceTable;
